import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";

interface GuestAuthFormProps {
  redirectPath: string;
  onSuccess?: () => void;
}

export default function GuestAuthForm({ redirectPath, onSuccess }: GuestAuthFormProps) {
  const { loginMutation, registerMutation, refetchUser } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === 'login') {
        console.log('[GuestAuthForm] Attempting login for', email);
        await loginMutation.mutateAsync({ username: email, password });
        console.log('[GuestAuthForm] Login success');
      } else {
        if (!fullName || !phone) {
          setError("Full name and phone number are required.");
          setLoading(false);
          return;
        }
        console.log('[GuestAuthForm] Attempting register for', email);
        await registerMutation.mutateAsync({
          username: email,
          email,
          password,
          confirmPassword: password,
          fullName,
          phone,
          role: 'guest',
          termsAccepted: true,
        });
        console.log('[GuestAuthForm] Register success');
      }
      // Refetch user to ensure state is up-to-date
      await refetchUser();
      // On success, call onSuccess if provided
      if (onSuccess) {
        console.log('[GuestAuthForm] Calling onSuccess callback');
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || "Failed to authenticate");
      console.log('[GuestAuthForm] Auth error', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Tabs for Login/Register */}
      <div className="flex mb-2 border-b">
        <button
          type="button"
          className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'login' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
          onClick={() => setMode('login')}
          disabled={loading}
        >
          Sign In
        </button>
        <button
          type="button"
          className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'register' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
          onClick={() => setMode('register')}
          disabled={loading}
        >
          Register
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-2">
        {mode === 'register' && (
          <>
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded"
            />
          </>
        )}
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          className="w-full px-3 py-2 border rounded"
        />
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          className="w-full px-3 py-2 border rounded"
        />
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <button
          type="submit"
          className="btn btn-primary w-full"
          disabled={loading}
        >
          {loading ? (mode === 'login' ? 'Logging in...' : 'Registering...') : (mode === 'login' ? 'Login' : 'Register')}
        </button>
      </form>
    </div>
  );
} 